<template>
  <!-- Badge flotante mientras corre - se ve en CUALQUIER pantalla (está
       montado una sola vez en App.vue, fuera del router-view), no solo en
       /carga. Si se minimiza el navegador ENTERO no hay forma de que se siga
       viendo (ver botón de "ventana flotante" para eso). -->
  <Teleport to="body">
    <div v-if="timerStore.isRunning && !timerStore.pipActivo" class="floating-timer">
      <i class="bi bi-record-fill floating-timer-dot"></i>
      <span class="floating-timer-time">{{ display }}</span>
      <span v-if="timerStore.proyecto" class="floating-timer-proj">{{ timerStore.proyecto.text }}</span>
      <button
        class="floating-timer-pip"
        title="Ventana flotante - se sigue viendo aunque minimices el navegador"
        @click="abrirPip"
      >
        <i class="bi bi-box-arrow-up-right"></i>
      </button>
      <button class="floating-timer-stop" title="Stop" @click="stop">
        <i class="bi bi-stop-fill"></i>
      </button>
    </div>
  </Teleport>

  <!-- Ventana Picture-in-Picture de documento (Chrome/Edge): ventana del
       sistema operativo aparte - sobrevive a minimizar el navegador entero
       y a navegar entre pantallas de la SPA (el timer vive en el store, no
       en ningún componente de ruta). Firefox/Safari no la soportan. -->
  <Teleport v-if="timerStore.pipTarget" :to="timerStore.pipTarget">
    <div class="pip-timer">
      <template v-if="resultado">
        <i class="bi" :class="resultado.ok ? 'bi-check-circle-fill pip-ok' : 'bi-info-circle-fill pip-info'"></i>
        <span class="pip-result-text">{{ resultado.mensaje }}</span>
      </template>
      <template v-else>
        <i class="bi bi-record-fill floating-timer-dot"></i>
        <span class="floating-timer-time">{{ display }}</span>
        <span v-if="timerStore.proyecto" class="floating-timer-proj">{{ timerStore.proyecto.text }}</span>
        <button class="floating-timer-stop" title="Stop" @click="stop">
          <i class="bi bi-stop-fill"></i>
        </button>
      </template>
    </div>
  </Teleport>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue'
import { storeToRefs } from 'pinia'
import Swal from 'sweetalert2'
import { useActiveTimerStore } from '../../stores/activeTimer.js'
import { useCatalogStore } from '../../stores/catalog.js'
import { useRunningReminder } from '../../composables/useRunningReminder.js'

const timerStore = useActiveTimerStore()
const catalogStore = useCatalogStore()
const { isRunning, elapsedSeconds } = storeToRefs(timerStore)

const { requestPermiso } = useRunningReminder(elapsedSeconds, isRunning)

const display = computed(() => {
  const h = Math.floor(elapsedSeconds.value / 3600)
  const m = Math.floor((elapsedSeconds.value % 3600) / 60)
  const s = Math.floor(elapsedSeconds.value % 60)
  const pad = (n) => String(n).padStart(2, '0')
  return `${pad(h)}:${pad(m)}:${pad(s)}`
})

// Título de la pestaña con el tiempo corriendo, visible desde cualquier
// pantalla (al pasar el mouse por la pestaña o alt-tabear), aunque el
// navegador esté minimizado.
const tituloOriginal = document.title
watch([() => timerStore.isRunning, display], ([running, texto]) => {
  document.title = running ? `⏱ ${texto} · Carga Diaria` : tituloOriginal
})
onBeforeUnmount(() => { document.title = tituloOriginal })

function formatDuracion(segundos) {
  const h = Math.floor(segundos / 3600)
  const m = Math.floor((segundos % 3600) / 60)
  if (h > 0) return `${h}h ${m}min`
  return `${m} min`
}

async function mostrarResultadoRecuperado(res, proyectoNombre) {
  if (res.ok) {
    Swal.fire({
      icon: 'success',
      title: 'Tiempo recuperado y cargado',
      text: `${res.tiempo} en ${proyectoNombre}`,
      timer: 3000,
      showConfirmButton: false
    })
  } else {
    Swal.fire({
      icon: 'error',
      title: 'No se pudo cargar el tiempo recuperado',
      text: res.error
    })
  }
}

function recuperarSesionColgada() {
  const recuperado = timerStore.recuperarDeStorage()
  if (!recuperado) return
  const { datos, segundos } = recuperado

  const p = catalogStore.proyectos.find(
    (pr) => pr.prID === datos.projectId && pr.tipo === datos.tipo
  )
  const duracion = formatDuracion(segundos)

  Swal.fire({
    icon: 'warning',
    title: 'Quedó un cronómetro corriendo',
    html: `La última vez quedó corriendo en <b>${datos.projectName}</b>${datos.desc ? ' — ' + datos.desc : ''}.<br>` +
      `Pasaron aproximadamente <b>${duracion}</b> desde entonces (puede incluir tiempo con la compu apagada/suspendida o la pestaña cerrada).`,
    showDenyButton: true,
    showCancelButton: true,
    confirmButtonText: `Cargar ${duracion}`,
    denyButtonText: 'Seguir corriendo',
    cancelButtonText: 'Descartar',
    reverseButtons: true
  }).then(async (result) => {
    if (result.isConfirmed) {
      const res = await timerStore.guardarTiempoDirecto(datos, segundos)
      await mostrarResultadoRecuperado(res, datos.projectName)
    } else if (result.isDenied && p) {
      requestPermiso()
      timerStore.reanudarConSegundos(p, datos, segundos)
    }
    // "Descartar" (cancel): ya se limpió el storage en recuperarDeStorage().
  })
}
onMounted(recuperarSesionColgada)

// Detección de hueco grande (pestaña/navegador vivo pero oculto/suspendido
// mucho tiempo mientras el timer corría) - se pregunta si hay que descontar
// ese lapso en vez de darlo por trabajado. Vive acá (global) en vez de en
// EntryBar.vue para que se detecte sin importar en qué pantalla se esté.
const UMBRAL_HUECO_SEGUNDOS = 5 * 60
let ocultoDesde = null

function onVisibilityChangeHueco() {
  if (document.visibilityState === 'hidden') {
    if (timerStore.isRunning) ocultoDesde = Date.now()
    return
  }
  if (ocultoDesde == null) return
  const hueco = Math.floor((Date.now() - ocultoDesde) / 1000)
  ocultoDesde = null
  if (!timerStore.isRunning || hueco < UMBRAL_HUECO_SEGUNDOS) return

  const duracion = formatDuracion(hueco)
  Swal.fire({
    icon: 'question',
    title: 'La compu estuvo inactiva',
    html: `Pasaron <b>${duracion}</b> sin ver esta pestaña (¿se suspendió la compu?) mientras el cronómetro seguía corriendo en <b>${timerStore.proyecto?.text}</b>.<br>¿Contamos ese tiempo como trabajado?`,
    showDenyButton: true,
    confirmButtonText: 'Sí, contarlo',
    denyButtonText: 'No, descontarlo'
  }).then((result) => {
    if (result.isDenied) timerStore.descontar(hueco)
  })
}
document.addEventListener('visibilitychange', onVisibilityChangeHueco)
onBeforeUnmount(() => document.removeEventListener('visibilitychange', onVisibilityChangeHueco))

// La ventana flotante (Document Picture-in-Picture) vive en el store
// (activeTimer.js) para que cualquier pantalla pueda abrirla - acá solo se
// la invoca y se muestra el resultado si falla.
async function abrirPip() {
  const res = await timerStore.togglePip()
  if (!res.ok) {
    Swal.fire({ icon: 'info', title: 'No disponible', text: res.error })
  }
}

onBeforeUnmount(() => {
  clearTimeout(resultadoTimeout)
  timerStore.cerrarPip()
})

// Resultado del último Stop, para mostrar dentro de la ventana PiP.
const resultado = ref(null)
let resultadoTimeout = null

function mostrarResultado(ok, mensaje) {
  if (!timerStore.pipActivo) {
    Swal.fire({
      icon: ok ? 'success' : 'info',
      title: ok ? 'Hora cargada' : 'Tiempo muy corto',
      text: mensaje,
      timer: 2500,
      showConfirmButton: false
    })
    return
  }
  resultado.value = { ok, mensaje }
  clearTimeout(resultadoTimeout)
  resultadoTimeout = setTimeout(() => {
    resultado.value = null
    timerStore.cerrarPip()
  }, 3000)
}

async function stop() {
  const res = await timerStore.stopAndSave()
  if (res.corto) {
    mostrarResultado(false, 'El tiempo registrado es muy corto, no se cargó.')
    return
  }
  if (!res.ok) {
    mostrarResultado(
      false,
      res.reintentable
        ? `No se pudo guardar (${res.error}) - sigue corriendo, reintentá en un rato.`
        : `No se pudo guardar: ${res.error}`
    )
    return
  }
  const verbo = res.sumado ? 'Sumado' : 'Guardado'
  mostrarResultado(true, `${verbo} ${res.tiempoCargado} en ${res.proyectoNombre}`)
}

</script>

<style scoped>
/* Badge flotante con el cronómetro - fijo en pantalla mientras corre. */
.floating-timer {
  position: fixed;
  right: 20px;
  bottom: 20px;
  z-index: 2000;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  color: #fff;
  background: #1f2937;
  border-radius: 999px;
  box-shadow: 0 4px 18px rgba(0, 0, 0, 0.35);
}
.floating-timer-dot {
  color: #dc3545;
  font-size: 0.6rem;
  animation: pulse-record 1.5s infinite;
}
@keyframes pulse-record {
  0%, 100% { box-shadow: 0 0 0 0 rgba(220, 53, 69, 0.5); }
  50% { box-shadow: 0 0 0 6px rgba(220, 53, 69, 0); }
}
.floating-timer-time {
  font-variant-numeric: tabular-nums;
  font-weight: 700;
  font-size: 1rem;
}
.floating-timer-proj {
  max-width: 160px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: var(--font-size-sm);
  color: #d1d5db;
}
.floating-timer-stop {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  color: #fff;
  background: #dc3545;
  border: none;
  border-radius: 50%;
  cursor: pointer;
}
.floating-timer-pip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  color: #fff;
  background: transparent;
  border: 1px solid #4b5563;
  border-radius: 50%;
  cursor: pointer;
}
.floating-timer-pip:hover {
  border-color: #9ca3af;
}

.pip-timer {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  height: 100%;
  padding: 0 12px;
  color: #fff;
  font-family: var(--font-family);
  box-sizing: border-box;
}
.pip-result-text {
  font-size: var(--font-size-sm);
  line-height: 1.3;
  text-align: center;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.pip-ok {
  color: #22c55e;
  font-size: 1.3rem;
  flex-shrink: 0;
}
.pip-info {
  color: #facc15;
  font-size: 1.3rem;
  flex-shrink: 0;
}
</style>
